import type {
  DesktopPraxaAccountControlsBridge,
  PraxaDesktopAccountControlsState,
  PraxaDesktopSmartNotificationPreferenceChange,
} from "@praxa/platform-contracts";
import { useCallback, useEffect, useRef, useState } from "react";

export const PRAXA_ACCOUNT_CONTROLS_LOAD_ERROR =
  "Praxa plan, usage, and notification settings couldn’t load.";
export const PRAXA_ACCOUNT_CONTROLS_ACTION_ERROR =
  "Praxa couldn’t save that notification setting. Check your connection and try again.";

function getBridge(): DesktopPraxaAccountControlsBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge?.praxaAccountControls;
}

export interface DesktopPraxaAccountControlsController {
  readonly actionError: string | null;
  readonly bridge: DesktopPraxaAccountControlsBridge | undefined;
  readonly clearActionError: () => void;
  readonly loading: boolean;
  readonly refresh: () => Promise<void>;
  readonly saving: boolean;
  readonly state: PraxaDesktopAccountControlsState | null;
  readonly updateNotifications: (
    change: PraxaDesktopSmartNotificationPreferenceChange,
  ) => Promise<PraxaDesktopAccountControlsState | null>;
}

export function useDesktopPraxaAccountControls(): DesktopPraxaAccountControlsController {
  const bridge = getBridge();
  const [state, setState] = useState<PraxaDesktopAccountControlsState | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const eventVersionRef = useRef(0);

  const applyState = useCallback((nextState: PraxaDesktopAccountControlsState) => {
    setState(nextState);
    if (nextState.status === "ready") setActionError(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!bridge) return;
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    setActionError(null);
    try {
      const nextState = await bridge.refresh();
      if (eventVersionRef.current === eventVersion) applyState(nextState);
    } catch {
      if (eventVersionRef.current === eventVersion) {
        setActionError(PRAXA_ACCOUNT_CONTROLS_LOAD_ERROR);
      }
    } finally {
      setLoading(false);
    }
  }, [applyState, bridge]);

  const updateNotifications = useCallback(
    async (
      change: PraxaDesktopSmartNotificationPreferenceChange,
    ): Promise<PraxaDesktopAccountControlsState | null> => {
      if (!bridge || saving) return null;
      const eventVersion = eventVersionRef.current;
      setSaving(true);
      setActionError(null);
      try {
        const nextState = await bridge.updateNotifications(change);
        if (eventVersionRef.current === eventVersion) applyState(nextState);
        if (nextState.status !== "ready") {
          setActionError(nextState.message ?? PRAXA_ACCOUNT_CONTROLS_ACTION_ERROR);
        }
        return nextState;
      } catch {
        if (eventVersionRef.current === eventVersion) {
          setActionError(PRAXA_ACCOUNT_CONTROLS_ACTION_ERROR);
        }
        return null;
      } finally {
        setSaving(false);
      }
    },
    [applyState, bridge, saving],
  );

  useEffect(() => {
    if (!bridge) {
      setState(null);
      setLoading(false);
      setSaving(false);
      setActionError(null);
      return;
    }

    let active = true;
    let unsubscribe = () => {};
    try {
      unsubscribe = bridge.onStateChange((nextState) => {
        eventVersionRef.current += 1;
        if (active) applyState(nextState);
      });
    } catch {
      setActionError(PRAXA_ACCOUNT_CONTROLS_LOAD_ERROR);
    }

    const eventVersion = eventVersionRef.current;
    setLoading(true);
    void bridge
      .getState()
      .then((nextState) => {
        if (active && eventVersionRef.current === eventVersion) applyState(nextState);
      })
      .catch(() => {
        if (active && eventVersionRef.current === eventVersion) {
          setActionError(PRAXA_ACCOUNT_CONTROLS_LOAD_ERROR);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [applyState, bridge]);

  return {
    actionError,
    bridge,
    clearActionError: () => setActionError(null),
    loading,
    refresh,
    saving,
    state,
    updateNotifications,
  };
}
